import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers } from '@fortawesome/free-solid-svg-icons'
import { ArrowsDownUp, PencilSimple } from '@phosphor-icons/react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { User } from '../../types/User'
import { parseTribes } from '../../types/Tribe'
import { Role } from '../../types/Roles'
import { FlagBadges } from '../MemberFlags/FlagBadges'

type SortColumn = 'name' | 'email' | 'guild' | 'tribe' | 'status'

const getStatus = (date?: Date) => {
  if (!date) return 'Active'
  const today = new Date()
  const userEndDate = new Date(date)
  return userEndDate < today ? 'Inactive' : 'Active'
}

interface Props {
  employees: User[]
  guilds: Role[]
  onEdit: (employee: User) => void
}

const MembersTable = ({ employees, guilds, onEdit }: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      let sortValueA = ''
      let sortValueB = ''
      if (sortColumn === 'name') {
        sortValueA = `${a.firstName} ${a.lastName}`
        sortValueB = `${b.firstName} ${b.lastName}`
      } else if (sortColumn === 'email') {
        sortValueA = a.email
        sortValueB = b.email
      } else if (sortColumn === 'status') {
        sortValueA = getStatus(a.end_on)
        sortValueB = getStatus(b.end_on)
      } else if (sortColumn === 'guild') {
        sortValueA = guilds.find(guild => guild.id === a.guildId)?.role ?? ''
        sortValueB = guilds.find(guild => guild.id === b.guildId)?.role ?? ''
      } else if (sortColumn === 'tribe') {
        sortValueA = parseTribes(a.tribes)
        sortValueB = parseTribes(b.tribes)
      }
      return sortOrder === 'asc'
        ? sortValueA.localeCompare(sortValueB)
        : sortValueB.localeCompare(sortValueA)
    })
  }, [employees, guilds, sortColumn, sortOrder])

  const sortableHeader = (label: string, column: SortColumn, width: string) => (
    <th
      className="sortable"
      style={{ width }}
      onClick={() => handleSort(column)}
    >
      {label}
      <ArrowsDownUp className="tm-sort-icon" size={14} weight="regular" />
    </th>
  )

  return (
    <div className="tm-table-wrap">
      <table className="tm-table">
        <thead>
          <tr>
            {sortableHeader('Name', 'name', '17%')}
            <th style={{ width: 130 }}>Flags</th>
            {sortableHeader('Email', 'email', '24%')}
            {sortableHeader('Guild', 'guild', '17%')}
            {sortableHeader('Tribe', 'tribe', '12%')}
            <th style={{ width: '10%' }}>Team</th>
            {sortableHeader('Status', 'status', '9%')}
            <th className="tm-table__edit-cell">{/* Edit */}</th>
          </tr>
        </thead>
        <tbody>
          {sortedEmployees.map(employee => {
            const userTribes = parseTribes(employee.tribes)
            const userTeams =
              employee.teamAllocations
                ?.map(allocation => allocation.team?.name || '')
                .filter(val => !!val)
                .join(', ') || ''
            const status = getStatus(employee.end_on)
            return (
              <tr key={employee.id}>
                <td className="tm-table__truncate">
                  {employee.firstName} {employee.lastName}
                </td>
                <td>
                  <FlagBadges user={employee} />
                </td>
                <td className="tm-table__truncate">{employee.email}</td>
                <td className="tm-table__truncate">
                  {guilds.find(guild => guild.id === employee.guildId)?.role ??
                    employee.guildId}
                </td>
                <td className="tm-table__truncate">{userTribes}</td>
                <td className="tm-table__truncate">{userTeams}</td>
                <td>
                  <span
                    className={`tm-status-badge ${
                      status === 'Active'
                        ? 'tm-status-badge--active'
                        : 'tm-status-badge--inactive'
                    }`}
                  >
                    {status}
                  </span>
                </td>
                <td className="tm-table__edit-cell">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`edit-tooltip-${employee.id}`}>
                        Edit
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      aria-label="Edit member"
                      className="tm-table__edit-btn"
                      onClick={() => onEdit(employee)}
                    >
                      <PencilSimple size={18} weight="regular" />
                    </button>
                  </OverlayTrigger>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {employees.length === 0 && (
        <div className="tm-empty-state">
          <FontAwesomeIcon icon={faUsers} size="2x" />
          <span>No members found. Try adjusting your search or filters.</span>
        </div>
      )}
    </div>
  )
}

export default MembersTable
export { getStatus }
